import { isFinite, omit } from "lodash";
import { DateTime } from "luxon";
import React, { FC } from "react";

import { Card, CardContent, Skeleton, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";

import useTimePeriod from "../../../hooks/use-time-period";
import { useSelector } from "../../../store";
import {
  GetTimeSeriesSumApiArg,
  TimeSeriesMetricId,
  useGetTimeSeriesSumQuery,
} from "../../../store/pomeriumApi";

interface Props {
  metricId: TimeSeriesMetricId;
  title: string;
}

const PeriodChange: FC<Props> = ({ metricId, title }) => {
  const { period } = useTimePeriod();
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const { routeId } = useSelector((state) => state.metrics);
  const now = DateTime.now().startOf("minute");
  const start = now.minus(period);
  const prevStart = start.minus(period);
  let params: GetTimeSeriesSumApiArg = {
    organizationId,
    clusterId,
    metricId,
    startTime: start.toISO(),
    endTime: now.toISO(),
    routeId: routeId,
  };
  let prevParams: GetTimeSeriesSumApiArg = {
    organizationId,
    clusterId,
    metricId,
    startTime: prevStart.toISO(),
    endTime: start.toISO(),
    routeId: routeId,
  };
  if (!routeId) {
    params = omit(params, ["routeId"]);
    prevParams = omit(prevParams, ["routeId"]);
  }

  const { data: timeSeries } = useGetTimeSeriesSumQuery(params, {
    skip: !organizationId || !clusterId,
  });
  const { data: prevTimeSeries } = useGetTimeSeriesSumQuery(prevParams, {
    skip: !organizationId || !clusterId,
  });

  const count = timeSeries?.reduce((count, row) => count + row.value, 0) || 0;

  const previousCount =
    prevTimeSeries?.reduce((count, row) => count + row.value, 0) || 0;

  const change =
    count && previousCount
      ? Math.round(((count - previousCount) / previousCount) * 100)
      : 0;
  const hasChange = isFinite(change) && change !== 0;

  const loading = !timeSeries || !prevTimeSeries;

  return (
    <Card
      sx={{
        width: "100%",
        m: 2,
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="center">
          <Typography variant={"h4"} color="text.primary" gutterBottom>
            {title}
          </Typography>
        </Stack>
        {loading && (
          <Skeleton width="100%" height="100%" sx={{ transform: "unset" }} />
        )}
        {!loading && (
          <Stack direction="row" justifyContent="center">
            <Typography
              variant="h4"
              color={isFinite(count) ? "textPrimary" : "textSecondary"}
              align={hasChange ? "right" : "center"}
            >
              {isFinite(count) ? Math.round(count).toLocaleString() : "No data"}
            </Typography>
            {hasChange && (
              <>
                <Typography
                  gutterBottom={false}
                  variant={"body2"}
                  sx={Object.assign(
                    {
                      display: "inline-flex",
                      padding: 0.25,
                      fontSize: ".8rem",
                      fontWeight: "bold",
                    },
                    {
                      color: change > 0 ? "success.dark" : "error.dark",
                    },
                  )}
                >
                  {change > 0 ? "↑" : "↓"}
                </Typography>
                <Typography
                  gutterBottom={false}
                  variant={"body2"}
                  sx={{
                    display: "inline-flex",
                    padding: 0.25,
                    fontSize: ".8rem",
                    fontWeight: "normal",
                    color: "textSecondary",
                  }}
                >
                  {change > 0 ? "+" : "-"}
                  {Math.round(
                    Math.abs(count - previousCount),
                  ).toLocaleString()}{" "}
                  ({change > 0 ? "+" : ""}
                  {change}
                  %)
                </Typography>
              </>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default PeriodChange;
